import React, { Component } from 'react';
import { Table, Badge } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ui from '../../common/services/ui';
import { normalize, schema } from 'normalizr';
import moment from 'moment';
import LabelWithTooltip from '../../common/components/LabelWithTooltip';
import { Checkbox } from 'react-icheck';
import 'icheck/skins/minimal/grey.css';
import { withTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBill1 } from '@fortawesome/free-regular-svg-icons';
import { faCamera, faMedkit, faCheckCircle, faTimesCircle } from '@fortawesome/free-solid-svg-icons';

const borderLine = '3px solid #ddd';

class WorkshopAttendanceTable extends Component {

	constructor(props) {
		super(props);

		this.handleChangeSelection = this.handleChangeSelection.bind(this);
	}

	getChildren() {
		const normalizedData = this.normalizeData(this.props.childSessions);
		return normalizedData && normalizedData.entities && normalizedData.entities.children;
	}

	normalizeData(childSessions) {
		const child = new schema.Entity('children', {}, {
			idAttribute: c => c.id
		});
		const session = new schema.Entity('childWorkshopSessions', { child: child }, {
			idAttribute: s => s.id
		});
		const normalizedData = normalize(childSessions, [session]);
		return normalizedData;
	}

	handleChangeSelection(children, checked) {
		const { selectedChildren, onMultipleSelectionChange } = this.props;
		if (children && onMultipleSelectionChange) {
			const currentSelection = selectedChildren ? [...selectedChildren] : [];

			children.forEach(child => {
				const existingRowIndex = currentSelection.findIndex(x => x.id === child.id);

				if (checked && existingRowIndex < 0) {
					currentSelection.push(child);
				}

				if (!checked && existingRowIndex >= 0) {
					currentSelection.splice(existingRowIndex, 1);
				}
			});

			onMultipleSelectionChange(currentSelection);
		}
	}

	renderChildrensSessions(selectedChildIds) {
		const { canSendMessage, canViewNotes, sessionDates, t } = this.props;
		const children = this.getChildren();
		let sortedChildren = children && Object.values(children).sort((a, b) => a.name.toUpperCase() < b.name.toUpperCase() ? -1 : 1);

		return sortedChildren && Object.keys(sortedChildren).map(key => {
			const child = sortedChildren[key];
			const checkboxCell = canSendMessage && this.renderCheckboxCell(child, selectedChildIds);
			const childCell = this.renderChildCell(child, t);
			const notesCell = canViewNotes && this.renderNotesCell(child);

			const sessionCells = sessionDates.map(date => this.renderSessionCell(date, child));

			return (
				<tr key={key}>
					{[checkboxCell, childCell, notesCell, sessionCells]}
				</tr>
			);
		});
	}

	renderCheckboxCell(child, selectedChildIds) {
		return <td key={`checkbox_${child.id}`} style={{ width: 20 }}>
			<Checkbox checkboxClass='icheckbox_minimal-grey bg' increaseArea='20%'
				checked={selectedChildIds.has(child.id)} onChange={(e, checked) => this.handleChangeSelection([child], checked)}
			/>
		</td>;
	}

	renderNotesCell(child) {
		const { canViewNotes, onNotesClick, t } = this.props;

		return <td key={`notes${child.id}`}>
			{canViewNotes && <div>
				<div style={{ whiteSpace: 'nowrap' }}>
					<a onClick={() => onNotesClick(child)} style={{ cursor: 'pointer' }}>{t('Child_Section_Notes')}</a>
					{child.noteStats && child.noteStats.count > 0 && <Badge className='badge-green' style={{ marginLeft: 3 }}>{child.noteStats.count}</Badge>}
				</div>
				<span className='text-muted' style={{ fontSize: '12px' }}>{child.noteStats.lastCreated && moment(child.noteStats.lastCreated).fromNow()}</span>
			</div>}
		</td>
	}

	renderChildCell(child, t) {
		const { childSessions, onInfoClick, enableCollectMedicalInformation } = this.props;
		const parentText = `${child.parent.name} >>`;
		const hasPendingApprovalChildSessions = childSessions.some(l => l.child.id === child.id && l.payment.status === 6);

		const medicalIconColor = child.hasAnyMedicalInformation ? 'red' : (child.completeMedicalInformationForm ? 'green' : 'orange');
		const medicalIconTooltip = child.hasAnyMedicalInformation ? 'Child_HasMedicalInformationWithIssuesTooltip' : (child.completeMedicalInformationForm ? 'Child_HasMedicalInformationTooltip' : 'Child_NoMedicalInformationTooltip');

		const medicalInfoIcon = enableCollectMedicalInformation && <LabelWithTooltip id='hasMedicalInformation' tooltip={t(medicalIconTooltip)}>
			<FontAwesomeIcon style={{ color: medicalIconColor, marginLeft: '5px' }} icon={faMedkit} />
		</LabelWithTooltip>;

		const pendingApprovalIcon = hasPendingApprovalChildSessions && <LabelWithTooltip id='pendingApproval' tooltip={t('Child_HasPendingApprovalTooltip')}>
			<FontAwesomeIcon style={{ color: 'red', marginLeft: '5px' }} icon={faMoneyBill1} />
		</LabelWithTooltip>;

		const photoConsentIcon = <LabelWithTooltip id='photoConsent' tooltip={t(child.parent.photoConsent ? 'Child_HasPhotoConsentTooltip' : 'Child_NoPhotoConsentTooltip')}>
			<FontAwesomeIcon style={{ color: child.parent.photoConsent ? 'green' : 'red', marginLeft: '5px' }} icon={faCamera} />
		</LabelWithTooltip>;

		return (
			<td key={child.id}>
				<div>
					<Link to={`/parents/${child.parent.id}/children/${child.id}`} style={{ fontWeight: 'bold' }}>{child.name}</Link>
					{enableCollectMedicalInformation && <Link to={`/parents/${child.parent.id}/children/${child.id}/medical`}>{medicalInfoIcon}</Link>}
					{photoConsentIcon}
					{pendingApprovalIcon}
					<div>
						<span style={{ whiteSpace: 'nowrap' }}>{ui.formatAge(child.dateOfBirth)}</span>
						<span className='text-muted' style={{ fontSize: '12px', marginLeft: 5 }}>{ui.formatShortDate(child.dateOfBirth)}</span>
					</div>
				</div>
				<div>
					<a onClick={() => onInfoClick(child.parent)} style={{ float: 'right', fontSize: '12px' }}>{parentText}</a>
				</div>
			</td>
		);
	}

	renderDates() {
		const { sessionDates, canSendMessage, canViewNotes } = this.props;

		return (
			<tr>
				{canSendMessage && <th key={-3} />}
				<th key={-2} />
				{canViewNotes && <th key={-1} />}
				{sessionDates && sessionDates.map((date, index) => <th colSpan={2} key={index} className={'center'} align='center' valign='middle'
					style={{ borderLeft: `${borderLine}`, borderRight: `${borderLine}` }}>
					{ui.customDateFormat(date, 'LL')}
				</th>)}
			</tr>
		);
	}

	renderCapacity(date, type) {
		const { childSessions, weekSessions, t } = this.props;
		const enrolledCount = childSessions.filter(l => l.session.date === date && (l.session.type === type || l.session.type === 3)).length;
		let weekSession = weekSessions.find(l => l.date === date && l.typeId === type);
		if (!weekSession) // if no morning/afternoon session, get fullday session capacity
		{
			weekSession = weekSessions.find(l => l.date === date && l.typeId === 3);
		}
		return <div><span>{`${enrolledCount}/${weekSession && weekSession.actualCapacity || 0}`}</span>
			<span className='hidden-xxs'>{t('WorkshopSession_Enrolled')}</span></div>
	}

	renderSessionTypes(selectedChildIds) {
		const { sessionDates, canSendMessage, canViewNotes, t } = this.props;
		const children = this.getChildren();
		const selected = children && Object.keys(children).every(id => selectedChildIds.has(Number(id)));

		return (
			<tr>
				{canSendMessage && <th key={-3} style={{ width: 20 }}>
					<Checkbox checkboxClass='icheckbox_minimal-grey bg' increaseArea='20%'
						checked={selected}
						onChange={(e, checked) => this.handleChangeSelection(children && Object.values(children), checked)}
					/>
				</th>}
				<th key={-2} />
				{canViewNotes && <th key={-1} />}
				{sessionDates && sessionDates.map((date, index) => [<th key={'m' + index} className='center' align='center' valign='middle' style={{ borderLeft: `${borderLine}` }}>
					<div><span className='hidden-xxs'>{t('WorkshopSessionType_Morning')}</span><span className='visible-xxs'>{t('WorkshopSessionType_Morning_Short')}</span></div>{this.renderCapacity(date, 1)}
				</th>, <th key={'a' + index} className={'center'} align='center' valign='middle' style={{ borderRight: `${borderLine}` }}>
					<div><span className='hidden-xxs'>{t('WorkshopSessionType_Afternoon')}</span><span className='visible-xxs'>{t('WorkshopSessionType_Afternoon_Short')}</span></div>{this.renderCapacity(date, 2)}
				</th>])}
			</tr>
		);
	}

	renderSessionCell(date, child) {
		const { childSessions } = this.props;

		const findSession = (sessionType) => childSessions.filter(l => l.child.id === child.id && l.session.date === date && l.session.type === sessionType);

		const morningSessions = findSession(1);
		const afternoonSessions = findSession(2);
		const fullDaySessions = findSession(3);
		const emptyCell = (index, leftBorder, rightBorder) => <td key={`${index}_${date}`}
			style={{ borderLeft: `${leftBorder ? `${borderLine}` : ''}`, borderRight: `${rightBorder ? `${borderLine}` : ''}` }} />;

		if (morningSessions.length > 0 && afternoonSessions.length > 0) {
			return [this.renderSessionCellIcon(morningSessions[0], 0, 1, 0),
			this.renderSessionCellIcon(afternoonSessions[0], 0, 0, 1)]
		}
		if (morningSessions.length > 0) {
			return [this.renderSessionCellIcon(morningSessions[0], 0, 1, 0), emptyCell(1, 0, 1)]
		}
		if (afternoonSessions.length) {
			return [emptyCell(1, 1, 0), this.renderSessionCellIcon(afternoonSessions[0], 0, 0, 1)]
		}
		if (fullDaySessions.length > 0) {
			return this.renderSessionCellIcon(fullDaySessions[0], 2, 1, 1)
		}
		return [emptyCell(1, 1, 0), emptyCell(2, 0, 1)]
	}

	renderSessionCellIcon(session, colSpan, leftBorder, rightBorder) {
		const { onSessionClick, isReadonly } = this.props;

		return <td colSpan={colSpan ? colSpan : 1} key={session.id} className={'center'} align='center' valign='middle'
			style={{ borderLeft: `${leftBorder ? `${borderLine}` : ''}`, borderRight: `${rightBorder ? `${borderLine}` : ''}` }}>
			<a onClick={() => isReadonly ? {} : onSessionClick(session)}>
				<FontAwesomeIcon icon={session.isAttended ? faCheckCircle : faTimesCircle } className={`attendance-icon ${session.isAttended ? 'green' : 'red'}`} />
			</a>
		</td>
	}

	render() {
		const { selectedChildren, showHeader } = this.props;

		const selectedChildIds = new Set(selectedChildren && [...selectedChildren.map(x => x.id)]);

		return (
			<Table bordered striped hover responsive>
				<thead>
					{showHeader && this.renderDates()}
					{this.renderSessionTypes(selectedChildIds)}
				</thead>
				<tbody>
					{this.renderChildrensSessions(selectedChildIds)}
				</tbody>
			</Table>
		);
	}
}

export default withTranslation()(WorkshopAttendanceTable);